import { generateKey } from '@util/generateKey';
import { useContext } from 'react';
import { updateJobSurveyTypes } from 'src/api/yembo/job';
import { generateDummyImage, useCreateImageBuilder } from 'src/queries/Image';
import { usePostItemsBuilder } from 'src/queries/Item';
import { usePostVideoBuilder } from 'src/queries/Video';
import { TextMessageContext } from 'src/react/_contexts/TextMessageContext';
import { useCurrentRef } from 'src/react/_hooks/useCurrentRef';
import { OnlineStatusContext } from 'src/react/_sw/OnlineStatusProvider/context';
import { loadImage } from 'src/react/_util/roomImages/loadImage';
import { createImageBlobForAI } from 'src/react/RoomReview/_shared/Camera';
import { detectDummyImage } from 'src/react/RoomReview/_shared/detectDummyImage';
import { getNewSnapshotTimestamp } from 'src/react/RoomReview/_shared/getNewSnapshotTimestamp';
import { Item, Room, SnapshotImage, Video } from 'src/types';

export const useJobImageToRoom = (): ((imageUrl: string, room: Room) => Promise<void>) => {
  const { isOnline } = useContext(OnlineStatusContext);
  const textMessageContext = useContext(TextMessageContext);
  const textMessageContextRef = useCurrentRef(textMessageContext);

  const postVideoBuilder = usePostVideoBuilder();
  const createImageBuilder = useCreateImageBuilder();
  const postTagsBuilder = usePostItemsBuilder();

  return async (imageUrl: string, room: Room) => {
    // Make this call first to ensure an image is loaded before any other manipulations
    const imageElement = await loadImage(imageUrl);

    const {
      blob: snapshotBlob,
      width: snapshotWidth,
      height: snapshotHeight,
    } = await createImageBlobForAI(imageElement);

    const _isSkipAnalysisRoom =
      textMessageContextRef.current.customizations[0]?.skipAnalysisRooms?.includes(room.name) ?? false;

    // Use values from ref because the room state can be updated while async calls above
    // This fixes issue with dummy image updated multiple times due to room state being out of sync
    const { job, videos, images, items: allTags } = textMessageContextRef.current;

    const roomVideo = videos.find(({ roomKey }) => roomKey === room.key);

    let roomVideoKey;
    let dummyImageKey;
    let roomImages;

    if (!roomVideo) {
      const video: Video = {
        key: generateKey({ type: 'video' }),
        roomKey: room.key,
        status: 'initialized_manual',
        degreesRotated: 0,
        createdAt: new Date().toISOString(),
      };

      const dummyImage = generateDummyImage({ videoKey: video.key });

      postVideoBuilder(room.key, job.key).execute({
        video,
        initialImage: dummyImage,
        skipAnalysis: true,
      });

      roomVideoKey = video.key;
      dummyImageKey = dummyImage.key;

      roomImages = images.filter((image) => image.videoKey === video.key);
    } else {
      roomImages = images.filter((image) => image.videoKey === roomVideo.key);
      const isDummyImage = detectDummyImage(roomImages);

      roomVideoKey = roomVideo.key;
      dummyImageKey = isDummyImage ? roomImages[0].key : undefined;
    }

    const _roomTags = allTags.filter(({ imageKey }) => roomImages.some(({ key }) => key === imageKey));

    const image: SnapshotImage = {
      key: dummyImageKey ?? generateKey({ type: 'image' }),
      videoKey: roomVideoKey,
      videoTimestamp: getNewSnapshotTimestamp(roomImages),
      url: URL.createObjectURL(snapshotBlob),
      width: snapshotWidth,
      height: snapshotHeight,
      status: 'new',
      annotationSource: 'human',
    };
    const items: Item[] = [];

    const createImagePromise = createImageBuilder(room.key, job.key).execute({
      image: { ...image, sourceUrl: imageUrl },
      isExistingImage: Boolean(dummyImageKey),
      imageBlobToUpload: snapshotBlob,
    });

    const postTagsPromise = postTagsBuilder(room.key, job.key).execute(items);

    if (!job.surveyTypes.includes('ai upload')) {
      updateJobSurveyTypes({
        job,
        shouldFetchJob: isOnline,
        surveyType: 'ai upload',
      });
    }

    await Promise.all([createImagePromise, postTagsPromise]);
  };
};
